import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

type Theme = 'light' | 'dark' | 'system';
type Font =
  | 'system' | 'onest' | 'inter' | 'geist' | 'geist_mono' | 'figtree' | 'lato'
  | 'lexend' | 'manrope' | 'mulish' | 'nunito' | 'open_sans' | 'outfit'
  | 'golos_text' | 'instrument_sans' | 'gabarito' | 'lora' | 'playfair'
  | 'neuton' | 'slabo' | 'enriqueta' | 'oswald' | 'fira_code'
  | 'fira_sans_condensed' | 'google_sans' | 'google_sans_code' | 'lilex'
  | 'gochi_hand' | 'sn_pro' | 'suse';
type Density = 'comfortable' | 'cozy';
type TicketView = 'peek' | 'modal';

interface ThemeContextType {
  theme: Theme;
  resolved: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  font: Font;
  setFont: (font: Font) => void;
  density: Density;
  setDensity: (density: Density) => void;
  ticketView: TicketView;
  setTicketView: (view: TicketView) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'kora_theme';
export const THEME_REFRESH_EVENT = 'kora:theme-refresh';
const FONT_KEY = 'kora_font';
const DENSITY_KEY = 'kora_density';
const TICKET_VIEW_KEY = 'kora_ticket_view';

const FONT_STACKS: Record<Font, string> = {
  system: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
  onest: 'Onest, Inter, ui-sans-serif, system-ui, sans-serif',
  inter: 'Inter, ui-sans-serif, system-ui, sans-serif',
  geist: 'Geist, ui-sans-serif, system-ui, sans-serif',
  geist_mono: 'Geist Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
  figtree: 'Figtree, ui-sans-serif, system-ui, sans-serif',
  lato: 'Lato, ui-sans-serif, system-ui, sans-serif',
  lexend: 'Lexend, ui-sans-serif, system-ui, sans-serif',
  manrope: 'Manrope, ui-sans-serif, system-ui, sans-serif',
  mulish: 'Mulish, ui-sans-serif, system-ui, sans-serif',
  nunito: 'Nunito, ui-sans-serif, system-ui, sans-serif',
  open_sans: 'Open Sans, ui-sans-serif, system-ui, sans-serif',
  outfit: 'Outfit, ui-sans-serif, system-ui, sans-serif',
  golos_text: 'Golos Text, ui-sans-serif, system-ui, sans-serif',
  instrument_sans: 'Instrument Sans, ui-sans-serif, system-ui, sans-serif',
  gabarito: 'Gabarito, ui-sans-serif, system-ui, sans-serif',
  lora: 'Lora, ui-serif, Georgia, serif',
  playfair: 'Playfair, ui-serif, Georgia, serif',
  neuton: 'Neuton, ui-serif, Georgia, serif',
  slabo: 'Slabo 13px, ui-serif, Georgia, serif',
  enriqueta: 'Enriqueta, ui-serif, Georgia, serif',
  oswald: 'Oswald, ui-sans-serif, system-ui, sans-serif',
  fira_code: 'Fira Code, ui-monospace, SFMono-Regular, Menlo, monospace',
  fira_sans_condensed: 'Fira Sans Condensed, ui-sans-serif, system-ui, sans-serif',
  google_sans: 'Google Sans, ui-sans-serif, system-ui, sans-serif',
  google_sans_code: 'Google Sans Code, ui-monospace, SFMono-Regular, Menlo, monospace',
  lilex: 'Lilex, ui-monospace, SFMono-Regular, Menlo, monospace',
  gochi_hand: 'Gochi Hand, ui-sans-serif, cursive',
  sn_pro: 'SN Pro, ui-sans-serif, system-ui, sans-serif',
  suse: 'SUSE, ui-sans-serif, system-ui, sans-serif',
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'system';
    return (localStorage.getItem(STORAGE_KEY) as Theme) || 'system';
  });
  const [resolved, setResolved] = useState<'light' | 'dark'>('light');
  const [font, setFontState] = useState<Font>(() => {
    if (typeof window === 'undefined') return 'onest';
    const saved = localStorage.getItem(FONT_KEY);
    // 'inter' was the old default: move it to Onest once, later picks of Inter stick.
    if (saved === 'inter' && !localStorage.getItem('kora_font_onest')) { localStorage.setItem('kora_font_onest', '1'); localStorage.removeItem(FONT_KEY); return 'onest'; }
    if (saved && saved in FONT_STACKS) return saved as Font;
    if (saved === 'mono') return 'geist_mono';
    return 'onest';
  });
  const [density, setDensityState] = useState<Density>(() => {
    if (typeof window === 'undefined') return 'comfortable';
    return (localStorage.getItem(DENSITY_KEY) as Density) || 'comfortable';
  });
  const [ticketView, setTicketViewState] = useState<TicketView>(() => {
    if (typeof window === 'undefined') return 'peek';
    return (localStorage.getItem(TICKET_VIEW_KEY) as TicketView) || 'peek';
  });

  useEffect(() => {
    const root = document.documentElement;
    const system = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const preferred = theme === 'system' ? (system.matches ? 'dark' : 'light') : theme;
      // Marketing pages are always light; MarketingLayout sets this flag while mounted.
      const resolvedTheme = root.dataset.forceLight === 'true' ? 'light' : preferred;
      setResolved(resolvedTheme);
      if (resolvedTheme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };
    apply();
    system.addEventListener('change', apply);
    window.addEventListener(THEME_REFRESH_EVENT, apply);
    return () => {
      system.removeEventListener('change', apply);
      window.removeEventListener(THEME_REFRESH_EVENT, apply);
    };
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.style.setProperty('--app-font', FONT_STACKS[font]);
    root.style.fontFamily = FONT_STACKS[font];
    body.style.fontFamily = FONT_STACKS[font];
  }, [font]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('density-comfortable', 'density-cozy');
    root.classList.add(`density-${density}`);
  }, [density]);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    localStorage.setItem(STORAGE_KEY, t);
  };

  const setFont = (f: Font) => {
    setFontState(f);
    localStorage.setItem(FONT_KEY, f);
    localStorage.setItem('kora_font_onest', '1');
  };

  const setDensity = (d: Density) => {
    setDensityState(d);
    localStorage.setItem(DENSITY_KEY, d);
  };

  const setTicketView = (v: TicketView) => {
    setTicketViewState(v);
    localStorage.setItem(TICKET_VIEW_KEY, v);
  };

  return <ThemeContext.Provider value={{ theme, resolved, setTheme, font, setFont, density, setDensity, ticketView, setTicketView }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

export type { Font, Density, TicketView };
